import React, { useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import SelectFixedLocation from "src/views/capture/RackLocations/StorageLocationAutocomplete/SelectFixedLocation";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { MOBILE_WAREHOUSE_LOCATION_TRANSFER } from "../../../constants/graphql/mutations";
import { MOBILE_WAREHOUSE_LOCATION } from "../../../constants/graphql/queries";
import MoveConfirmationModal from "./MoveConfirmationModal";

const initialLocation = { warehouse: null, warehouse_locations: [] };

const locationsVariables = {
  excludeType: "base",
  order: ["location_id", "asc"],
  projectIds: [],
};

export default function MoveEmptyMobileLocationModal({ onClose, warehouseId, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [selectedMobileLocation, setSelectedMobileLocation] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [newLocationNames, setNewLocationNames] = useState([]);
  const [warehouseName, setWarehouseName] = useState("");
  const vars = warehouseId ? { ...locationsVariables, warehouseId } : locationsVariables;
  const apolloClient = useApolloClient();
  const [mobileWarehouseLocationTransfer] = useMutation(MOBILE_WAREHOUSE_LOCATION_TRANSFER);
  const { notifyError } = useNotification();

  const fetchMobileWarehouseLocation = async id => {
    const { data } = await apolloClient.query({
      query: MOBILE_WAREHOUSE_LOCATION,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data?.mobileWarehouseLocation;
  };

  const handleSubmit = () => {};

  const handleCloseConfirmationModal = () => {
    setConfirmationOpen(false);
    onClose();
  };

  const handleChange = (value, details) => {
    setSelectedLocation(value);
    const mobileLocationData = { ...value, locationName: details.locationName, details };
    setSelectedMobileLocation(mobileLocationData);
  };

  const handleMoveToNewLocation = async (newLocationData, mobileLocation, details) => {
    setLoading(true);
    try {
      const mobileLocationId = selectedMobileLocation.details.locations[0].id;
      const newWarehouseLocationId = details.locations[0].id;

      const mobileWarehouseLocation = await fetchMobileWarehouseLocation(mobileLocationId);
      const variables = {
        id: mobileLocationId,
        newWarehouseLocationId,
      };

      // Only include oldWarehouseLocationId if there's an existing assignment
      if (
        mobileWarehouseLocation.warehouseLocations &&
        mobileWarehouseLocation.warehouseLocations.length > 0
      ) {
        variables.oldWarehouseLocationId = mobileWarehouseLocation.warehouseLocations[0].id;
      }

      await mobileWarehouseLocationTransfer({
        variables,
        onSuccess: data => {
          if (!data.success) {
            notifyError("Failed to move mobile location. Please try again.");
            return;
          }

          const updatedMobileLocation = data.mobileWarehouseLocation;
          const updatedLocations = updatedMobileLocation?.warehouseLocations || [];
          const names = updatedLocations.map(l => l.locationName);
          const firstWarehouse = updatedMobileLocation?.warehouse?.name || "";
          
          setNewLocationNames(names);
          setWarehouseName(firstWarehouse);
          setConfirmationOpen(true);
          if (onSubmit) onSubmit();
        },
      });
    } catch (error) {
      console.error("Error moving mobile location:", error);
      notifyError("Failed to move mobile location. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography fontSize={24} fontWeight={400} align="center" color="text.primary">
            Select Mobile Location
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <StorageLocationAutocomplete
              autoOpen
              locationsVariables={vars}
              warehouseId={warehouseId}
              enableMobileWarehouseModal={false}
              value={selectedLocation}
              onChange={handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={onClose} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {selectedMobileLocation && (
        <SelectFixedLocation
          location={selectedMobileLocation}
          onClose={() => setSelectedMobileLocation(null)}
          onSubmit={handleMoveToNewLocation}
        />
      )}
      {confirmationOpen && (
        <MoveConfirmationModal
          open={confirmationOpen}
          onClose={handleCloseConfirmationModal}
          itemType="mobile"
          locations={newLocationNames}
          warehouseName={warehouseName}
        />
      )}
    </>
  );
}
